import { EscalationStrategy } from "../release/Release.model";

export type EscalationRuleCustomFieldItem = {
  key: string;
  value: string;
  // set = inclusive range [value, valueTo], absent = equality
  valueTo?: string | null;
  operator?: string | null;
};

export type EscalationCustomFieldFilter = {
  value: string;
  valueTo?: string;
  operator?: string;
};

export type EscalationCustomFieldFilters = Record<
  string,
  EscalationCustomFieldFilter
>;

function isValidStoredValue(value: string): boolean {
  return value !== "" && value !== "undefined";
}

function stripCompositeKey(compositeKey: string): string {
  return compositeKey.replace(/::(?:gte|lte)$/, "");
}

export function buildCustomFieldsFromFilters(
  customFieldFilters: EscalationCustomFieldFilters | undefined
): EscalationRuleCustomFieldItem[] {
  if (!customFieldFilters) return [];
  return Object.entries(customFieldFilters)
    .filter(([key, filter]) => key !== "" && isValidStoredValue(filter.value))
    .map(([compositeKey, filter]) => ({
      key: stripCompositeKey(compositeKey),
      value: filter.value,
      // omitted, not nulled, so equality rows keep their existing wire shape
      ...(filter.valueTo != null && isValidStoredValue(filter.valueTo)
        ? { valueTo: filter.valueTo }
        : {}),
      ...(filter.operator != null ? { operator: filter.operator } : {}),
    }));
}

export function buildCustomFieldFiltersFromCustomFields(
  customFields: EscalationRuleCustomFieldItem[] | undefined
): EscalationCustomFieldFilters {
  if (!customFields?.length) return {};
  const result: EscalationCustomFieldFilters = {};
  for (const { key, value, valueTo, operator } of customFields) {
    if (key !== "" && isValidStoredValue(value)) {
      // Use composite keys for gte/lte so multiple conditions on the same
      // field can coexist in the Record (e.g. "after" + "before").
      const filterKey =
        operator === "gte" || operator === "lte" ? `${key}::${operator}` : key;
      result[filterKey] = {
        value,
        ...(valueTo != null && isValidStoredValue(valueTo) ? { valueTo } : {}),
        ...(operator != null ? { operator } : {}),
      };
    }
  }
  return result;
}

export interface EscalationRule {
  id?: string;
  name: string;
  topics: string[];
  customFields?: EscalationRuleCustomFieldItem[];
  escalationStrategy: EscalationStrategy;
  systemMessage: string;
  simulatorId?: string;
  subject?: string;
  liveAgentRoutingStrategy?: LiveAgentRoutingStrategy;
  escalationRuleOperations: {
    type: EscalationRuleOperationType;
    enabled: boolean;
    key: string | null;
    value: string;
  }[];
}

export enum EscalationRuleOperationType {
  ADD_TAG = "ADD_TAG",
  ADD_CUSTOM_FIELD = "ADD_CUSTOM_FIELD",
  ADD_PRIORITY = "ADD_PRIORITY",
  ADD_ASSIGNEE = "ADD_ASSIGNEE",
  ADD_SUBJECT = "ADD_SUBJECT", // Add new operation type
}

export enum LiveAgentRoutingStrategy {
  BUSINESS_HOURS = "BUSINESS_HOURS",
  ONLINE_AGENTS = "ONLINE_AGENTS",
}
